import {
  DeleteOutlined,
  EditOutlined,
  EyeFilled,
  UploadOutlined
} from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Select as AntdSelect,
  Button,
  Checkbox,
  Flex,
  Form,
  Space,
  Tag,
  Tooltip,
  Typography,
  Upload
} from 'antd';
import { ProgressBar } from 'jodit/esm/modules';
import { find, isArray, isFunction } from 'lodash';
import { ArrowClockwise, Export, FileArrowDown, Plus } from 'phosphor-react';
import React, { useContext, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AppContext } from '../../AppContext';
import videoPlaceholder from '../../assets/images/imagePlaceholder.png';
import {
  ASSET_CATEGORY,
  CLOUD_FRONT_CACHE_KEYS,
  EXPORT_JOB_TYPES,
  IMPORT_JOBS_STATIC_FILES_URL,
  LIST_TYPES,
  MODULES,
  ORDER,
  PAGES_SORT_FIELD,
  ROLE_KEYS,
  ROUTES,
  UI_LABEL_TYPE,
  UI_LABEL_TYPES,
  WORKSPACE_ROLE_LEVEL,
  WORKSPACE_ROLE_PERMISSION
} from '../../common/constants';
import { fileUpload, getImageUrl } from '../../common/utils';
import { modalContext } from '../../components/AppComponentContainer';
import FilterBar, { useFilterBar } from '../../components/FilterBar';
import Image from '../../components/Image';
import ModalComponent from '../../components/ModalComponent';
import PageHeader from '../../components/PageHeader';
import PageList from '../../components/PageList';
import useCheckPermission from '../../hooks/useCheckPermission';
import { INVALIDATE_CLOUD_FRONT_CACHE } from '../menus/graphql/Mutations';
import AddEditUiLabelGroupModal from '../pages/component/ui-labels-group/AddEditUiLabelGroupModal';
import SelectUiLabelGroup from '../pages/component/ui-labels-group/SelectUiLabelGroup';
import { DELETE_UI_LABEL_GROUP } from '../pages/graphql/Mutations';
import { Select } from '../videos/components/FormInputs';
import {
  CREATE_IMPORT_JOB,
  DELETE_UI_LABEL,
  EXPORT_UI_LABELS
} from './graphql/Mutations';
import {
  GET_LANGUAGES,
  GET_UI_LABELS,
  IMPORT_JOB_SIGNED_URL
} from './graphql/Queries';

const variablesSelector = ({
  limit,
  offset,
  search,
  languageCode,
  labelType,
  labelGroupId
}) => ({
  filter: {
    skip: offset,
    search,
    limit,
    languageCode,
    labelType,
    labelGroupId,
    includeAutoGenerated: true
  }
});

const dataSelector = ({ uiLabelsAdmin }) => ({
  data: uiLabelsAdmin?.uiLabels ?? [],
  count: uiLabelsAdmin?.count ?? 0
});

const Action = ({ id, refresh, isAutoGenerated, uiLabelKey, languageCode }) => {
  const {
    state: { currentUser: { roles = [] } = null }
  } = useContext(AppContext);

  const [deleteUILabel] = useMutation(DELETE_UI_LABEL);

  const history = useHistory();
  const isViewOnly = useCheckPermission([
    {
      moduleKey: WORKSPACE_ROLE_PERMISSION.UI_CONFIG_MANAGEMENT,
      allowedPermissions: [WORKSPACE_ROLE_LEVEL.VIEW]
    }
  ]);

  const handleClick = () => {
    history.push(`${ROUTES?.UI_LABELS}/${id}/edit`);
  };

  const handleDelete = () => {
    let allLanguages = false;
    let modalInstance;

    const renderContent = (checked) => (
      <Space direction="vertical" size="small">
        <Typography.Text type={checked ? 'danger' : 'secondary'}>
          {checked
            ? `This will delete "${uiLabelKey}" in ALL languages. This action cannot be undone.`
            : `This will delete "${uiLabelKey}" only for the "${languageCode}" language.`}
        </Typography.Text>
        <Checkbox
          onChange={(e) => {
            allLanguages = e.target.checked;
            modalInstance?.update({ content: renderContent(allLanguages) });
          }}
        >
          Delete this label in all languages
        </Checkbox>
      </Space>
    );

    modalInstance = modalContext?.confirm({
      title: 'Are you sure, you want to delete this UI Label?',
      centered: true,
      okText: 'Yes',
      cancelText: 'No',
      okType: 'primary',
      content: renderContent(allLanguages),
      onOk() {
        deleteUILabel({
          variables: { id, allLanguages }
        })
          .then(() => {
            if (refresh) {
              refresh();
            }
          })
          .catch();
      }
    });
  };

  return (
    <>
      <Tooltip
        title={`${isViewOnly || isAutoGenerated ? 'View' : 'Edit'} UI Label${
          isAutoGenerated ? ' (Auto Generated)' : ''
        }`}
      >
        <Button type="text" className="text-btn" onClick={handleClick}>
          {isViewOnly || isAutoGenerated ? <EyeFilled /> : <EditOutlined />}
        </Button>
      </Tooltip>
      {!isAutoGenerated &&
        [ROLE_KEYS.ADMIN, ROLE_KEYS.SUPER_ADMIN]?.includes(roles?.[0]) && (
          <Tooltip title="Delete UI Label">
            <Button type="text" className="text-btn" onClick={handleDelete}>
              <DeleteOutlined />
            </Button>
          </Tooltip>
        )}
    </>
  );
};

const columns = ({ showPreview, refresh }) => [
  {
    title: 'Reference Image',
    dataIndex: 'referenceImage',
    ellipsis: true,
    width: 150,
    render: (_, { referenceImage, value, key }) => (
      <Image
        className="group-img-table pointer"
        blurHash={referenceImage?.blurhash}
        src={
          getImageUrl(referenceImage?.url, { height: 50, width: 80 }) ??
          videoPlaceholder
        }
        alt={value}
        onClick={() => {
          if (isFunction(showPreview))
            showPreview({
              title: key,
              url: referenceImage?.url ?? videoPlaceholder,
              type: ASSET_CATEGORY.IMAGE
            });
        }}
      />
    )
  },
  {
    title: 'Key',
    dataIndex: 'key',
    render: (value, { isAutoGenerated }) => (
      <Space direction="vertical" size={2}>
        <span>{value}</span>
        {isAutoGenerated && (
          <Tag className="m-0 custom-tag" color="blue">
            Auto Generated
          </Tag>
        )}
      </Space>
    )
  },
  {
    title: 'Value',
    dataIndex: 'value',
    render: (value, { type }) =>
      type === UI_LABEL_TYPES.HTML ? (
        <div
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: value
          }}
        />
      ) : (
        value
      )
  },
  {
    title: 'Description',
    dataIndex: 'description'
  },
  {
    title: 'Type',
    dataIndex: 'type',
    width: 150
  },
  {
    title: 'Label Group',
    dataIndex: ['labelGroup', 'name'],
    key: 'labelGroup',
    width: 140,
    render: (value) => value ?? '-'
  },
  {
    title: 'Action',
    dataIndex: 'id',
    render: (_, { id, isAutoGenerated, key, languageCode }) => (
      <Action
        id={id}
        refresh={refresh}
        isAutoGenerated={isAutoGenerated}
        uiLabelKey={key}
        languageCode={languageCode}
      />
    ),
    width: 150
  }
];

const SelectUILabelGroup = ({ value, onChange }) => {
  const ref = useRef(null);
  const [modalState, setModalState] = useState(null);

  const handleSuccess = () => {
    if (ref.current?.refetch) {
      ref.current.refetch();
    }
  };

  const [deletePageGroup] = useMutation(DELETE_UI_LABEL_GROUP, {
    onCompleted() {
      handleSuccess();
    },
    onError() {}
  });

  const handleClose = () => {
    setModalState({
      open: false,
      data: null
    });
  };

  const handleAddGroup = () => {
    setModalState({
      open: true,
      data: ''
    });
  };

  const handleDelete = (id) => {
    if (!id) return;
    modalContext?.confirm({
      title: 'Are you sure, you want to delete this page group?',
      centered: true,
      okText: 'Yes',
      cancelText: 'No',
      onOk() {
        deletePageGroup({
          variables: {
            where: { id }
          }
        });
      }
    });
  };

  return (
    <>
      <AddEditUiLabelGroupModal
        {...modalState}
        onClose={handleClose}
        onSuccess={handleSuccess}
      />
      <SelectUiLabelGroup
        ref={ref}
        value={value}
        onChange={onChange}
        dropdownRender={(menu) => {
          return (
            <div>
              <Button
                className="select-add-btn"
                type="text"
                icon={<Plus size={22} />}
                onClick={handleAddGroup}
                onMouseDown={(e) => e.stopPropagation()}
              >
                Create group
              </Button>
              {menu}
            </div>
          );
        }}
        optionRender={(option) => (
          <Flex
            className="group-select-option"
            align="center"
            justify="space-between"
            gap={10}
          >
            <span className="mr-16">{option?.label}</span>
            <Flex align="center">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  setModalState({
                    open: true,
                    data: {
                      name: option?.label ?? '',
                      id: option?.value ?? ''
                    }
                  });
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                }}
              />
              <Button
                type="text"
                icon={<DeleteOutlined />}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(option?.value);
                }}
              />
            </Flex>
          </Flex>
        )}
      />
    </>
  );
};

const UILabels = () => {
  const [form] = Form.useForm();
  const [language, setLanguage] = useState(null);
  const [labelType, setLabelType] = useState('UI');
  const history = useHistory();
  const [importModal, setImportModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const showProgress = progress !== 0 && progress !== 100;
  const [uiLabelGroup, setUiLabelGroup] = useState(null);

  const isAddEditAllowed = useCheckPermission([
    {
      moduleKey: WORKSPACE_ROLE_PERMISSION.UI_CONFIG_MANAGEMENT,
      allowedPermissions: [
        WORKSPACE_ROLE_LEVEL.EDIT,
        WORKSPACE_ROLE_LEVEL.DELETE
      ]
    }
  ]);

  const isViewOnly = useCheckPermission([
    {
      moduleKey: WORKSPACE_ROLE_PERMISSION.UI_CONFIG_MANAGEMENT,
      allowedPermissions: [WORKSPACE_ROLE_LEVEL.VIEW]
    }
  ]);

  const isExportAllowed = useCheckPermission([
    {
      moduleKey: WORKSPACE_ROLE_PERMISSION.REPORT_MANAGEMENT,
      allowedPermissions: [
        WORKSPACE_ROLE_LEVEL.EDIT,
        WORKSPACE_ROLE_LEVEL.DELETE
      ]
    }
  ]);

  const filterProps = useFilterBar({
    defaultSortOptions: {
      options: PAGES_SORT_FIELD,
      order: ORDER[1].value,
      field: PAGES_SORT_FIELD[0].value
    }
  });

  const filters = useMemo(
    () => ({
      ...filterProps.filters,
      labelType,
      languageCode: language?.value || 'en',
      labelGroupId: uiLabelGroup?.value ?? null
    }),
    [filterProps.filters, labelType, uiLabelGroup, language]
  );

  const [invalidateCache, { loading: isInvalidating }] = useMutation(
    INVALIDATE_CLOUD_FRONT_CACHE,
    {
      variables: { key: CLOUD_FRONT_CACHE_KEYS.UI_LABELS }
    }
  );
  const [exportUiLabels, { loading: exportLoading }] = useMutation(
    EXPORT_UI_LABELS
  );
  const [getSignedUrl] = useLazyQuery(IMPORT_JOB_SIGNED_URL, {
    fetchPolicy: 'network-only'
  });
  const [createJob] = useMutation(CREATE_IMPORT_JOB);
  const handleExport = () => {
    exportUiLabels({
      variables: {
        where: {
          languageCode: language?.value,
          type: labelType
        }
      }
    });
  };

  const handleImportVideos = () => {
    setImportModal(true);
  };

  const handleShowModal = () => {
    history.push(`${ROUTES?.UI_LABELS}/add`);
  };

  const handleCancel = () => {
    setImportModal(false);
    form?.resetFields();
  };

  const handleLanguageChange = (_, newValue) => {
    setLanguage(newValue);
  };

  const handleSubmit = async ({ csv }) => {
    setLoading(true);
    const payload = { key: '', uid: '', type: EXPORT_JOB_TYPES?.UI_LABEL };
    try {
      if (csv?.length > 0) {
        const file = csv?.[0]?.originFileObj;
        const { data, error } = await getSignedUrl({
          variables: {
            data: {
              type: EXPORT_JOB_TYPES?.UI_LABEL
            }
          }
        });
        if (error) {
          throw new Error(error);
        }
        if (data?.importJobCsvUploadSignedUrl) {
          await fileUpload(
            data?.importJobCsvUploadSignedUrl?.signedUrl,
            file,
            setProgress
          );
          payload.key = data?.importJobCsvUploadSignedUrl?.key;
          payload.uid = data?.importJobCsvUploadSignedUrl?.uid;
          const { errors } = await createJob({
            variables: {
              data: payload
            }
          });
          if (errors) {
            throw new Error(errors);
          }
          setImportModal(false);
          form?.resetFields();
        }
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  };

  const handleGroupChange = (_, value) => setUiLabelGroup(value);

  return (
    <>
      <PageHeader
        handleShowModal={handleShowModal}
        menu={MODULES?.UI_CONFIGURATION}
        isAddEditAllowed={isAddEditAllowed}
      />
      <FilterBar
        {...filterProps}
        SearchProps={{ placeholder: 'Search UI Labels' }}
        show={{ listModes: false, sort: false }}
      >
        <SelectUILabelGroup value={uiLabelGroup} onChange={handleGroupChange} />
        <Space size="small" wrap>
          <Typography.Text className="sort-by-title" type="secondary">
            Type
          </Typography.Text>
          <AntdSelect
            popupMatchSelectWidth={false}
            value={labelType}
            onChange={setLabelType}
          >
            {UI_LABEL_TYPE?.map(({ label, value }) => (
              <AntdSelect.Option key={value} value={value}>
                {label}
              </AntdSelect.Option>
            ))}
          </AntdSelect>
          <Typography.Text className="sort-by-title" type="secondary">
            Language
          </Typography.Text>
          <Select
            searchable={false}
            popupMatchSelectWidth={false}
            value={language}
            placeholder="Language"
            query={GET_LANGUAGES}
            onChange={handleLanguageChange}
            dataSelector={(data) =>
              data?.languages?.languages?.map(({ id, code, name }) => ({
                id,
                label: name,
                value: code
              })) || []
            }
            keys={{
              data: 'languages',
              records: 'languages',
              count: 'count'
            }}
            queryOptions={{
              fetchPolicy: 'cache-first',
              onCompleted: ({ languages }) => {
                const englishLang = find(
                  languages?.languages,
                  // by default english from backend
                  (item) => ['en', 'EN']?.includes(item?.code)
                );
                setLanguage({
                  label: englishLang?.name,
                  value: englishLang?.code
                });
              }
            }}
          />
          <Tooltip title="Revalidate cache" placement="left">
            <Button
              className="text-btn d-flex align-center justify-center"
              type="text"
              icon={<ArrowClockwise size={24} />}
              onClick={invalidateCache}
              loading={isInvalidating}
            />
          </Tooltip>
          {isExportAllowed && (
            <Tooltip title="Export Ui Labels" placement="left">
              <Button
                className="text-btn d-flex align-center justify-center"
                type="text"
                icon={<Export size={24} weight="thin" />}
                onClick={handleExport}
                loading={exportLoading}
              />
            </Tooltip>
          )}
          <div className="pointer">
            <Button className="mr-8" onClick={handleImportVideos}>
              Import
            </Button>
          </div>
        </Space>
      </FilterBar>
      <PageList
        filters={filters}
        listMode={LIST_TYPES.TABLE}
        query={GET_UI_LABELS}
        variablesSelector={variablesSelector}
        dataSelector={dataSelector}
        TableProps={{
          columns: (props) =>
            columns({ ...props, isAddEditAllowed })?.filter(
              (item) => item !== false
            )
        }}
        keyField="id"
      />
      {importModal && (
        <ModalComponent
          destroyOnClose
          open={importModal}
          footer={null}
          onCancel={handleCancel}
        >
          <Form
            form={form}
            onFinish={handleSubmit}
            name="create-asset"
            layout="vertical"
            className="add-edit-form"
            initialValues={{ csv: [] }}
          >
            <Form.Item
              name="csv"
              label="File"
              rules={[{ required: true, message: 'Please select file!' }]}
              getValueFromEvent={(e) => {
                if (isArray(e)) {
                  return e;
                }
                return e?.fileList;
              }}
              valuePropName="fileList"
              extra={
                <a
                  className="d-flex align-center mt-8"
                  download
                  href={`${
                    process.env.REACT_APP_STATIC_DATA_URL
                  }/static/import-jobs/samples/${
                    IMPORT_JOBS_STATIC_FILES_URL[EXPORT_JOB_TYPES?.UI_LABEL]
                  }`}
                >
                  Sample file <FileArrowDown className="ml-8" size={22} />
                </a>
              }
            >
              <Upload
                maxCount={1}
                disabled={isViewOnly || loading}
                accept=".csv"
                beforeUpload={() => false}
              >
                <Space>
                  <Button icon={<UploadOutlined />}>Click to Upload</Button>
                  <span>(.csv)</span>
                </Space>
              </Upload>
            </Form.Item>

            {showProgress && (
              <Form.Item>
                <ProgressBar progress={progress} />
              </Form.Item>
            )}
            <div className="d-flex button-section">
              <Space>
                <Form.Item>
                  <Button
                    loading={loading}
                    type="text"
                    htmlType="submit"
                    className="text-btn mr-8"
                    size="middle"
                  >
                    Save
                  </Button>
                </Form.Item>
                <Form.Item>
                  <Button
                    type="text"
                    className="text-btn2"
                    disabled={loading}
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                </Form.Item>
              </Space>
            </div>
          </Form>
        </ModalComponent>
      )}
    </>
  );
};

export default UILabels;
